import { useState } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import {
  Home,
  Users,
  UserCog,
  CreditCard,
  Apple,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@shared/lib/utils";

type SidebarItem = {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  panelMatch?: string;
};

const items: SidebarItem[] = [
  { id: "home", label: "Home", to: "/", icon: Home },
  { id: "students", label: "Students", to: "/?panel=families", icon: Users, panelMatch: "families" },
  { id: "employees", label: "Employees", to: "/?panel=employees", icon: UserCog, panelMatch: "employees" },
  { id: "billing", label: "Billing", to: "/?panel=billing", icon: CreditCard, panelMatch: "billing" },
  { id: "cacfp", label: "CACFP", to: "/?panel=cacfp", icon: Apple, panelMatch: "cacfp" },
  { id: "reports", label: "Reports", to: "/?panel=reports", icon: BarChart3, panelMatch: "reports" },
  { id: "settings", label: "Settings", to: "/settings", icon: Settings },
];

const COLLAPSE_KEY = "sidebar_collapsed";

function getCachedCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
}

function setCachedCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "true" : "false");
  } catch {
    // ignore
  }
}

function isActive(item: SidebarItem, pathname: string, panel: string | null): boolean {
  if (item.id === "home") {
    return pathname === "/" && !panel;
  }
  if (item.panelMatch) {
    return pathname === "/" && panel === item.panelMatch;
  }
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

export function AdminSidebar() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const panel = searchParams.get("panel");
  const [collapsed, setCollapsed] = useState<boolean>(() => getCachedCollapsed());

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      setCachedCollapsed(next);
      return next;
    });
  };

  return (
    <aside
      className={cn(
        "sticky top-20 self-start h-[calc(100vh-5rem)] flex flex-col",
        "border-r border-border/40 bg-bg",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-56",
      )}
      aria-label="Primary navigation"
    >
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <ul className="flex flex-col gap-1">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item, location.pathname, panel);
            return (
              <li key={item.id}>
                <NavLink
                  to={item.to}
                  title={collapsed ? item.label : undefined}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    collapsed && "justify-center px-0",
                    active
                      ? "bg-surface2 text-accent"
                      : "text-muted hover:bg-surface2/50 hover:text-foreground",
                  )}
                >
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <button
        type="button"
        onClick={toggle}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cn(
          "m-2 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium",
          "text-muted hover:bg-surface2/50 hover:text-foreground transition-colors",
          collapsed && "justify-center px-0",
        )}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}
